import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  HStack,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import TrackingPageLayout from "components/TrackingPageLayout";
import { usePermissions } from "hooks/usePermissions";
import { useAuthentication } from "hooks/useAuthentication";
import useSWR from "swr";
import { fetchAdminStudents, type AdminStudentsListResponse, type MyStudent } from "services/tracking/studentsService";
import AppQrCode from "components/AppQrCode";

export default function TrackingStudentsListPage() {
  const navigate = useNavigate();
  const { has } = usePermissions();
  const { user } = useAuthentication();
  const token = user?.auth?.access_token;
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [qrStudent, setQrStudent] = useState<MyStudent | null>(null);

  const { data, isLoading, error } = useSWR<AdminStudentsListResponse>(
    token ? ["tracking-admin-students", page, pageSize, token] : null,
    () => fetchAdminStudents(token, { page, page_size: pageSize }),
    { revalidateOnFocus: false }
  );

  const students = data?.results ?? [];
  const total = data?.count ?? 0;
  const hasNext = data?.next != null;
  const hasPrev = data?.previous != null;

  const canCreateStudent = has("create tracking_student");

  return (
    <TrackingPageLayout
      title="Élèves – Administration"
      action={
        canCreateStudent ? (
          <Flex gap={2}>
            <Button size="xl" variant="outline" colorScheme="red_origin" fontWeight={700} borderRadius="8px" onClick={() => navigate("/tracking/students/add")}>
              Ajouter un élève
            </Button>
            <Button size="xl" variant="outline" colorScheme="red_origin" fontWeight={700} borderRadius="8px" onClick={() => navigate("/tracking/students/add/upload")}>
              Import CSV
            </Button>
          </Flex>
        ) : undefined
      }
    >
      {error && (
        <Box mb={4} p={4} bg="red.50" borderRadius="md" mx={4} mt={4}>
          <Text color="red.700">
            Erreur lors du chargement des élèves. Vérifiez que l&apos;API Tracking est disponible et que vous avez les droits admin.
          </Text>
        </Box>
      )}
      {isLoading && students.length === 0 ? (
        <Flex justify="center" py={10}>
          <Spinner size="lg" />
        </Flex>
      ) : students.length === 0 ? (
        <Box p={6} textAlign="center">
          <Text color="gray.700">Aucun élève trouvé.</Text>
        </Box>
      ) : (
        <>
          <Box overflowX="auto" px={4} pb={4}>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Nom</Th>
                  <Th>Institution</Th>
                  <Th>Adresse</Th>
                  <Th>Statut paiement</Th>
                  <Th>Dû</Th>
                  <Th>Disponibilité</Th>
                  <Th>QR</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(students as MyStudent[]).map((s) => {
                  const fullName =
                    (s.first_name || s.last_name)
                      ? `${s.first_name ?? ""} ${s.last_name ?? ""}`.trim()
                      : s.username ?? "Sans nom";
                  return (
                    <Tr key={s.id}>
                      <Td>
                        <Box>
                          <Text fontWeight="semibold">{fullName}</Text>
                          {s.parent_name && (
                            <Text fontSize="xs" color="gray.500">
                              Parent : {s.parent_name}
                            </Text>
                          )}
                        </Box>
                      </Td>
                      <Td>{s.institution_name ?? "—"}</Td>
                      <Td>{s.address_label ?? "—"}</Td>
                      <Td>{s.payment_status ?? "—"}</Td>
                      <Td>
                        {s.payment_due != null ? `${Number(s.payment_due).toFixed(2)} FCFA` : "—"}
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          {s.is_available !== undefined && (
                            <Badge colorScheme={s.is_available ? "green" : "orange"}>
                              {s.is_available ? "Disponible" : "Indisponible"}
                            </Badge>
                          )}
                          {s.has_active_trip && <Badge colorScheme="blue">Trajet en cours</Badge>}
                        </HStack>
                      </Td>
                      <Td>
                        {(s.code_identifier != null || s.student_uuid != null) ? (
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => {
                              setQrStudent(s);
                              onOpen();
                            }}
                          >
                            Voir QR
                          </Button>
                        ) : (
                          <Text fontSize="xs" color="gray.500">—</Text>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
          <Flex justify="space-between" align="center" mt={4} px={4} pb={4}>
            <Text fontSize="sm" color="gray.600">
              Page {page} – {students.length} élèves sur {total}
            </Text>
            <HStack spacing={2}>
              <Button
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                isDisabled={!hasPrev || page <= 1}
              >
                Précédent
              </Button>
              <Button
                size="sm"
                onClick={() => setPage((p) => p + 1)}
                isDisabled={!hasNext}
              >
                Suivant
              </Button>
            </HStack>
          </Flex>
        </>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Code QR – {qrStudent
              ? (qrStudent.first_name || qrStudent.last_name
                  ? `${qrStudent.first_name ?? ""} ${qrStudent.last_name ?? ""}`.trim()
                  : qrStudent.username ?? "Élève")
              : ""}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            {qrStudent && (qrStudent.code_identifier != null || qrStudent.student_uuid != null) && (
              <Flex direction="column" align="center" gap={3}>
                <AppQrCode
                  value={String(qrStudent.code_identifier ?? qrStudent.student_uuid ?? "")}
                />
                <Text fontSize="xs" color="gray.600">
                  Code : {String(qrStudent.code_identifier ?? qrStudent.student_uuid ?? "")}
                </Text>
              </Flex>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </TrackingPageLayout>
  );
}

